import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useParams, Redirect } from 'react-router-dom';
import { getHaito } from 'config/api';
import { formatNumber, formatDate } from 'utils/formatter';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import Loading from 'components/commons/loading/Loading';
import {
  DividendListProps,
  DividendListItemProps,
  UnreleasedStatusProps,
  DividendPredictionModal,
} from 'components/features/dividendPrediction/DividendClassification';
import { getConvertedAccountType } from 'components/features/utils/converter';
import { getSummaryDividendHaito } from 'components/features/dividendPrediction/DividendTransition';
import {
  DividendPieChartNoData,
  DividendPieChart,
} from 'components/features/dividendPrediction/DividendChart';
import { DividendSymbolList } from 'components/features/dividendDetailPrediction/DividendSymbolList';
import {
  DividendNoData,
  DividendPredictionLoading,
} from 'components/features/dividendPrediction/DividendError';
import { isIosApp } from 'components/features/utils/userStatus';
import {
  DividendHeader,
  DividendProps,
  UpdateTimeComment,
} from './DividendPrediction';
import { convertyyyymmToDate } from './DividendDeposit';
import { HightDividendList } from './DividendDetailDeposit';

export const formatYM = (ym: string) => {
  const yyyy = ym.substr(0, 4);
  const mm = ym.substr(4, 2);
  const space = Number(mm) < 10 ? ' ' : '';
  return `${yyyy}年${space}${Number(mm).toString()}月`;
};

export const convertToDisplayDividendList = (
  dividendData: DividendProps[],
  isTaxDeduction: boolean
): DividendListProps[] => {
  // memo: symbolとaccountTypeが同じデータでサマる
  const dividendSymbolList = Array.from(
    new Set(
      dividendData.map(item => {
        return { symbol: item.symbol, symbolName: item.symbolName };
      })
    )
  );

  const uniqueArray = Array.from(
    new Map(dividendSymbolList.map(item => [item.symbolName, item])).values()
  );

  const targetDividendList = uniqueArray.map(list => {
    return dividendData.filter(
      data => data.symbol === list.symbol && data.symbolName === list.symbolName
    );
  }) as DividendProps[][];

  const summaryDividendList = targetDividendList.map(dividendList => {
    const convertDividendList = dividendList.map(list => {
      return {
        ...list,
        accountType: getConvertedAccountType(list.accountType),
      };
    });

    const accountTypeList = Array.from(
      new Set(convertDividendList.map(item => item.accountType))
    );
    const summaryDividendList = accountTypeList.map(type => {
      const sameAccountTypeList = convertDividendList.filter(
        list => list.accountType === type
      );

      if (sameAccountTypeList.length === 1) {
        return {
          accountType: sameAccountTypeList[0].accountType,
          symbol: sameAccountTypeList[0].symbol,
          symbolName: sameAccountTypeList[0].symbolName,
          haito: sameAccountTypeList[0].haito,
          taxExcludedHaito: sameAccountTypeList[0].taxExcludedHaito,
          qty: sameAccountTypeList[0].qty,
          haitoYm: sameAccountTypeList[0].haitoYm,
          hasNew: sameAccountTypeList[0].hasNew,
          unreleasedStatus: sameAccountTypeList[0].unreleased
            ? 'all'
            : 'not_exist',
        } as DividendListItemProps;
      } else {
        const getUnreleasedStatus = (): UnreleasedStatusProps => {
          const isAllDataUnreleased = sameAccountTypeList.every(
            list => list.unreleased
          );
          const isIncludeDataUnreleased = sameAccountTypeList.some(
            list => list.unreleased
          );

          return isAllDataUnreleased && isIncludeDataUnreleased
            ? 'all'
            : !isAllDataUnreleased && isIncludeDataUnreleased
            ? 'not_all'
            : 'not_exist';
        };

        // memo: qtyはHaitoYmが最大のデータを採用
        const latestData = sameAccountTypeList.reduce((prev, current) => {
          const getDateTime = (haitoYm: string): number | undefined => {
            return convertyyyymmToDate(haitoYm)?.getTime();
          };

          const a = getDateTime(prev.haitoYm);
          const b = getDateTime(current.haitoYm);

          if (a === undefined || b === undefined) return prev;
          if (a === b || a > b) {
            return prev;
          } else if (a < b) {
            return current;
          }
          return prev;
        }, sameAccountTypeList[0]);

        return {
          accountType: sameAccountTypeList[0].accountType,
          symbol: sameAccountTypeList[0].symbol,
          symbolName: sameAccountTypeList[0].symbolName,
          haitoYm: sameAccountTypeList[0].haitoYm,
          hasNew: sameAccountTypeList[0].hasNew,
          qty: latestData.qty,
          haito: sameAccountTypeList.reduce(
            (acc, current) => acc + current.haito,
            0
          ),
          taxExcludedHaito: sameAccountTypeList.reduce(
            (acc, current) => acc + current.taxExcludedHaito,
            0
          ),
          unreleasedStatus: getUnreleasedStatus(),
        } as DividendListItemProps;
      }
    });

    const summaryQty = summaryDividendList.reduce(
      (acc, current): number => acc + current.qty,
      0
    );

    return {
      symbol: summaryDividendList[0].symbol,
      symbolName: summaryDividendList[0].symbolName,
      totalQty: summaryQty,
      items: summaryDividendList as DividendListItemProps[],
    } as DividendListProps;
  });

  const sortedDividendData = summaryDividendList.sort((first, second) => {
    const firstDividend = getSummaryDividendHaito(first.items, isTaxDeduction);
    const secondDevidend = getSummaryDividendHaito(
      second.items,
      isTaxDeduction
    );

    if (firstDividend > secondDevidend) {
      return -1;
    } else if (firstDividend < secondDevidend) {
      return 1;
    } else {
      return 0;
    }
  }) as DividendListProps[];

  return sortedDividendData;
};

const DividendDetailPredictionInternal = ({
  dividendData,
  date,
  year,
}: {
  dividendData: DividendProps[];
  date: string;
  year: string;
}) => {
  const displayPeriodMonth = 12;
  const dateStates = [...Array(displayPeriodMonth)].map((_, i) => {
    const month = (i + 1).toString();
    const date = `${year}${month.padStart(2, '0')}`;
    return date;
  });
  const [selectedDate, setSelectedDate] = useState<string>(date); // yyyymm

  const [displayDividendList, setDisplayDividendList] = useState<
    DividendListProps[] | null
  >(null);
  const [totalDividend, setTotalDividend] = useState<number | null>(null);

  const [isTaxDeduction, setIsTaxDeduction] = useState<boolean>(false);
  const [isFirst, setIsFirst] = useState<boolean>(true);

  useEffect(() => {
    (async () => {
      const targetDateDividendData = dividendData.filter(
        data => data.haitoYm === selectedDate
      );

      const data = convertToDisplayDividendList(
        targetDateDividendData,
        isTaxDeduction
      );
      setDisplayDividendList(data);

      const totalDividend = getSummaryDividendHaito(
        targetDateDividendData,
        isTaxDeduction
      );
      setTotalDividend(totalDividend);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    if (isFirst) {
      setIsFirst(false);
      return;
    }

    const dividendData_ =
      displayDividendList === null ? [] : displayDividendList;
    const dividendData = dividendData_.map(data => data.items).flat();
    const totalDividend = getSummaryDividendHaito(dividendData, isTaxDeduction);
    setTotalDividend(totalDividend);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTaxDeduction]);

  if (displayDividendList === null || totalDividend === null)
    return <DividendPredictionLoading str={'銘柄'} />;

  return (
    <>
      <DividendHeader {...{ type: 'prediction' }} />
      <div className="bg-white text-bluegray-700">
        <div className="relative flex justify-center items-center pt-6 mx-4">
          <div
            className="relative w-full flex flex-row justify-center items-center mx-auto"
            style={{ width: '140px' }}>
            <select
              className={`h-full w-full pl-2 appearance-none bg-transparent text-base font-bold cursor-pointer`}
              value={selectedDate}
              onChange={(e): void => {
                setSelectedDate(e.currentTarget.value);
              }}>
              {dateStates.map((s, i) => {
                return (
                  <option key={i} value={s.toString()}>
                    {`${formatYM(s)}`}
                  </option>
                );
              })}
            </select>
            <div
              className={`absolute icon-arrow_drop_down pointer-events-none p-0.5 text-2xl`}
              style={{ right: '6%' }}></div>
          </div>
          <div
            className="absolute flex flex-col justify-center items-center"
            style={{ right: '2%' }}>
            <div className="text-1.5xs">税引き計算</div>
            <div className="toggle ml-2">
              <div className="checkbox">
                <input
                  className="toggle-lg"
                  type="checkbox"
                  checked={isTaxDeduction}
                  onChange={() => setIsTaxDeduction(prev => !prev)}
                />
                <label className="toggle-lg" htmlFor={``}></label>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 text-center text-xs">
          <span className="mr-1 text-2xl font-bold">
            {formatNumber(totalDividend, {})}
          </span>
          円
        </div>
        <div className="my-2 mx-1">
          {displayDividendList.length === 0 ? (
            <DividendPieChartNoData />
          ) : (
            <DividendPieChart
              {...{
                dividendList: displayDividendList,
                selectedDate,
                isTaxDeduction,
              }}
            />
          )}
        </div>
        <div className="mx-4 mt-4 text-xs text-bluegray-550">
          ※ 未発表は過去データを元に算出
        </div>
        <DividendPredictionModal />
        <DividendSymbolList
          {...{
            displayDividendList,
            totalDividend,
            isTaxDeduction,
          }}
        />
      </div>
      <UpdateTimeComment />
      <HightDividendList {...{ tag: 'prediction', className: 'mt-8 ml-4' }} />
    </>
  );
};

const getYear = (date: string) => {
  const haitoDate = convertyyyymmToDate(date);
  return formatDate(haitoDate, 'yyyy');
};

const DividendDetailPrediction = (): React.ReactElement => {
  const { setWithBackIconWord } = useCustomizeHeaderContext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(setWithBackIconWord, []);

  const { date } = useParams<{ date: string }>();
  const year = getYear(date);

  const { data } = useQuery(
    'getHaitoDividendDetailPrediction',
    () => getHaitoAPI(),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (isIosApp()) return <Redirect to="/home" />;

  if (data === undefined)
    return (
      <Loading
        style={{ marginTop: '100px', marginBottom: '100px' }}
        loading={true}
      />
    );
  if (data.length === 0) return <DividendNoData />;

  const dividendData = data.filter((data: DividendProps) => {
    const haitoY = getYear(data.haitoYm);

    return year === haitoY && data.haito !== 0;
  }) as DividendProps[];

  return <DividendDetailPredictionInternal {...{ dividendData, date, year }} />;
};

const getHaitoAPI = async () => {
  const ret_ = await getHaito();
  const ret = ret_.Items.map(item => {
    return {
      symbol: item.Symbol,
      symbolName: item.SymbolName,
      haito: item.Haito,
      taxExcludedHaito: item.TaxExcludedHaito,
      qty: item.Qty,
      accountType: item.AccountType,
      // memo: apiレスポンスを yyyy.mm -> yyyymm に変換する
      haitoYm: item.HaitoYm.replace('.', ''),
      unreleased: item.Unreleased,
      hasNew: item.HasNew,
    };
  }) as DividendProps[];

  return ret;
};

export default DividendDetailPrediction;
